/**
 * Capsule 2 — Skills Taxonomy + Question Bank + Assessment Builder admin tab.
 * Combined into a single component with internal sub-tabs to ship fast.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { BookOpen, Plus, Trash2, Sparkles, Copy, Check, MessageSquare, ChevronRight, Link as LinkIcon, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const DIMENSIONS = ["Cognitive", "Relational", "Communication", "Leadership", "Developmental", "Execution", "Technical"];

function SubTabs({ tab, setTab }) {
  const tabs = [
    { id: "skills", name: "Skills Taxonomy" },
    { id: "questions", name: "Question Bank" },
    { id: "assessments", name: "Assessments" },
  ];
  return (
    <div className="flex gap-1 bg-white rounded-lg p-1 w-fit shadow-sm">
      {tabs.map(t => (
        <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tax-tab-${t.id}`}
          className={`font-dm text-sm px-4 py-2 rounded-lg ${tab === t.id ? "bg-navy text-white" : "text-navy/50 hover:text-navy"}`}>
          {t.name}
        </button>
      ))}
    </div>
  );
}

function SkillsTab() {
  const [skills, setSkills] = useState([]);
  const [dim, setDim] = useState("");
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [newSkill, setNewSkill] = useState({ skill_name: "", dimension: "Cognitive", description: "" });

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/taxonomy/skills`, { params: { dimension: dim || undefined, q: q || undefined }, withCredentials: true });
      setSkills(r.data.skills || []);
    } catch { /* */ }
  };

  useEffect(() => { load(); }, [dim, q]);

  const addSkill = async () => {
    if (newSkill.skill_name.length < 3) return alert("Name too short");
    try {
      await axios.post(`${API}/api/admin/taxonomy/skills`, newSkill, { withCredentials: true });
      setNewSkill({ skill_name: "", dimension: "Cognitive", description: "" });
      setShowAdd(false);
      load();
    } catch (err) { alert(err?.response?.data?.detail || "Failed"); }
  };

  const previewAnchors = async (skill_code) => {
    try {
      const r = await axios.get(`${API}/api/taxonomy/anchors/${skill_code}`);
      const anchors = r.data.anchors || {};
      alert([1,2,3,4,5].map(z => `Zone ${z}: ${anchors[`zone_${z}`] || "—"}`).join("\n\n"));
    } catch (err) { alert(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="skills-tab">
      <div className="flex flex-wrap gap-3 items-center">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search skill..."
          className="border border-navy/15 rounded-lg px-3 py-2 font-dm text-sm flex-1 min-w-[180px]" />
        <select value={dim} onChange={e => setDim(e.target.value)} className="border border-navy/15 rounded-lg px-3 py-2 font-dm text-sm">
          <option value="">All dimensions</option>
          {DIMENSIONS.map(d => <option key={d}>{d}</option>)}
        </select>
        <button onClick={() => setShowAdd(true)} data-testid="add-skill-btn"
          className="bg-navy text-gold font-dm font-bold text-sm px-4 py-2 rounded-lg flex items-center gap-2"><Plus className="w-4 h-4" /> Add Skill</button>
      </div>
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <table className="w-full">
          <thead><tr className="bg-navy/5 border-b border-navy/10">
            <th className="text-left font-dm text-navy/60 text-xs py-2 px-3">Code</th>
            <th className="text-left font-dm text-navy/60 text-xs py-2 px-3">Skill</th>
            <th className="text-left font-dm text-navy/60 text-xs py-2 px-3">Dimension</th>
            <th className="text-left font-dm text-navy/60 text-xs py-2 px-3">Source</th>
            <th className="text-right font-dm text-navy/60 text-xs py-2 px-3">Actions</th>
          </tr></thead>
          <tbody>
            {skills.map(s => (
              <tr key={s.skill_code} className="border-b border-navy/5 hover:bg-cream/50">
                <td className="font-mono text-navy/40 text-[11px] py-2 px-3">{s.skill_code}</td>
                <td className="font-dm text-navy text-sm font-semibold py-2 px-3">{s.skill_name}</td>
                <td className="font-dm text-navy/70 text-xs py-2 px-3">{s.dimension}</td>
                <td className="py-2 px-3"><span className={`font-dm text-[10px] px-2 py-0.5 rounded-full ${s.source === "platform" ? "bg-navy/5 text-navy" : "bg-gold/15 text-gold"}`}>{s.source}</span></td>
                <td className="text-right py-2 px-3">
                  <button onClick={() => previewAnchors(s.skill_code)} className="text-gold hover:underline font-dm text-xs font-bold flex items-center gap-1 ml-auto" data-testid={`preview-anchors-${s.skill_code}`}>
                    <Sparkles className="w-3 h-3" /> Anchors
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="font-playfair text-navy text-xl font-bold mb-4">New Custom Skill</h3>
            <input value={newSkill.skill_name} onChange={e => setNewSkill({...newSkill, skill_name: e.target.value})} placeholder="Skill name" className="w-full border rounded-lg px-3 py-2 mb-3 font-dm text-sm" data-testid="new-skill-name" />
            <select value={newSkill.dimension} onChange={e => setNewSkill({...newSkill, dimension: e.target.value})} className="w-full border rounded-lg px-3 py-2 mb-3 font-dm text-sm">
              {DIMENSIONS.map(d => <option key={d}>{d}</option>)}
            </select>
            <textarea value={newSkill.description} onChange={e => setNewSkill({...newSkill, description: e.target.value})} placeholder="Description (optional)" rows={2} className="w-full border rounded-lg px-3 py-2 mb-3 font-dm text-sm" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowAdd(false)} className="font-dm text-navy/60 px-4 py-2">Cancel</button>
              <button onClick={addSkill} data-testid="save-skill-btn" className="bg-navy text-gold font-dm font-bold px-4 py-2 rounded-lg">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function QuestionsTab() {
  const [questions, setQuestions] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newQ, setNewQ] = useState({ skill_code: "", question_type: "STAR", question_text: "" });
  const [skills, setSkills] = useState([]);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/question-bank`);
      setQuestions(r.data || []);
    } catch { /* */ }
  };

  useEffect(() => {
    load();
    axios.get(`${API}/api/taxonomy/skills`).then(r => setSkills(r.data.skills || [])).catch(() => {});
  }, []);

  const addQ = async () => {
    if (!newQ.skill_code || newQ.question_text.length < 10) return alert("Fill skill and question");
    try {
      await axios.post(`${API}/api/admin/question-bank`, newQ, { withCredentials: true });
      setNewQ({ skill_code: "", question_type: "STAR", question_text: "" });
      setShowAdd(false);
      load();
    } catch (err) { alert(err?.response?.data?.detail || "Failed"); }
  };

  const delQ = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await axios.delete(`${API}/api/admin/question-bank/${id}`, { withCredentials: true }); load(); }
    catch (err) { alert(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="questions-tab">
      <button onClick={() => setShowAdd(true)} data-testid="add-question-btn"
        className="bg-navy text-gold font-dm font-bold text-sm px-4 py-2 rounded-lg flex items-center gap-2"><Plus className="w-4 h-4" /> Add Question</button>
      <div className="space-y-2">
        {questions.map(q => (
          <div key={q._id} className="bg-white rounded-xl shadow-sm p-4 flex items-start gap-3" data-testid={`question-card-${q._id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="font-dm text-[10px] px-2 py-0.5 rounded-full bg-gold/10 text-gold font-bold">{q.question_type}</span>
                <span className="font-dm text-navy/60 text-xs">{q.skill_name} · {q.skill_code}</span>
                <span className={`font-dm text-[10px] px-2 py-0.5 rounded-full ${q.source === "platform" ? "bg-navy/5 text-navy" : "bg-gold/15 text-gold"}`}>{q.source}</span>
              </div>
              <p className="font-dm text-navy text-sm">{q.question_text}</p>
            </div>
            <button onClick={() => delQ(q._id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <h3 className="font-playfair text-navy text-xl font-bold mb-4">New Question</h3>
            <select value={newQ.skill_code} onChange={e => setNewQ({...newQ, skill_code: e.target.value})} className="w-full border rounded-lg px-3 py-2 mb-3 font-dm text-sm" data-testid="new-question-skill">
              <option value="">— select skill —</option>
              {skills.map(s => <option key={s.skill_code} value={s.skill_code}>{s.skill_name} ({s.dimension})</option>)}
            </select>
            <select value={newQ.question_type} onChange={e => setNewQ({...newQ, question_type: e.target.value})} className="w-full border rounded-lg px-3 py-2 mb-3 font-dm text-sm">
              <option value="STAR">STAR (Situation/Task/Action/Result)</option>
              <option value="OpenText">Open Text (free response)</option>
            </select>
            <textarea value={newQ.question_text} onChange={e => setNewQ({...newQ, question_text: e.target.value})} rows={4} placeholder="The question..." className="w-full border rounded-lg px-3 py-2 mb-3 font-dm text-sm" data-testid="new-question-text" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowAdd(false)} className="font-dm text-navy/60 px-4 py-2">Cancel</button>
              <button onClick={addQ} className="bg-navy text-gold font-dm font-bold px-4 py-2 rounded-lg" data-testid="save-question-btn">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AssessmentsTab() {
  const [assessments, setAssessments] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [copied, setCopied] = useState("");

  const load = async () => {
    try { const r = await axios.get(`${API}/api/assessments-builder/mine`, { withCredentials: true }); setAssessments(r.data || []); }
    catch { /* */ }
  };
  useEffect(() => { load(); }, []);

  const copyLink = (token) => {
    const url = `${window.location.origin}/assess/${token}`;
    navigator.clipboard.writeText(url);
    setCopied(token);
    setTimeout(() => setCopied(""), 2500);
  };

  return (
    <div className="space-y-4" data-testid="assessments-tab">
      <button onClick={() => setShowCreate(true)} data-testid="new-assessment-btn"
        className="bg-navy text-gold font-dm font-bold text-sm px-4 py-2 rounded-lg flex items-center gap-2"><Plus className="w-4 h-4" /> New Assessment</button>
      {assessments.length === 0 ? (
        <p className="font-dm text-navy/40 text-sm text-center py-10">No assessments yet. Click "New Assessment" to build your first one.</p>
      ) : (
        <div className="space-y-3">
          {assessments.map(a => (
            <div key={a._id} className="bg-white rounded-xl shadow-sm p-4" data-testid={`assessment-card-${a._id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <h4 className="font-dm text-navy font-bold text-sm">{a.title}</h4>
                  <p className="font-dm text-navy/50 text-xs">{a.role_title} · {a.seniority_level} · {a.skills?.length || 0} skills · {a.submission_count || 0} submissions</p>
                </div>
                <button onClick={() => copyLink(a.public_token)} data-testid={`copy-link-${a._id}`}
                  className="bg-cream border border-navy/15 text-navy font-dm font-bold text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-navy/5">
                  {copied === a.public_token ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy link</>}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {showCreate && <CreateAssessmentModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function CreateAssessmentModal({ onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState("");
  const [role, setRole] = useState("");
  const [seniority, setSeniority] = useState("Mid");
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [allSkills, setAllSkills] = useState([]);
  const [questionsBySkill, setQuestionsBySkill] = useState({});
  const [selectedQs, setSelectedQs] = useState({});

  useEffect(() => {
    axios.get(`${API}/api/taxonomy/skills`).then(r => setAllSkills(r.data.skills || []));
  }, []);

  const toggleSkill = (s) => {
    setSelectedSkills(prev => prev.find(x => x.skill_code === s.skill_code)
      ? prev.filter(x => x.skill_code !== s.skill_code)
      : [...prev, { ...s, weight: 2 }]);
  };

  const loadQuestions = async () => {
    const out = {};
    for (const s of selectedSkills) {
      const r = await axios.get(`${API}/api/question-bank?skill_code=${s.skill_code}`);
      out[s.skill_code] = r.data || [];
    }
    setQuestionsBySkill(out);
  };

  const toggleQ = (skillCode, qId) => {
    setSelectedQs(prev => ({
      ...prev,
      [skillCode]: (prev[skillCode] || []).includes(qId)
        ? (prev[skillCode] || []).filter(x => x !== qId)
        : [...(prev[skillCode] || []), qId],
    }));
  };

  const save = async () => {
    const payload = {
      title, role_title: role, seniority_level: seniority,
      skills: selectedSkills.map(s => ({ skill_code: s.skill_code, weight: s.weight, question_ids: selectedQs[s.skill_code] || [] })),
    };
    try {
      await axios.post(`${API}/api/assessments-builder`, payload, { withCredentials: true });
      onCreated();
    } catch (err) { alert(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-3xl w-full my-8" onClick={e => e.stopPropagation()} data-testid="create-assessment-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-playfair text-navy text-xl font-bold">Create Assessment — Step {step}/3</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-navy/40" /></button>
        </div>
        {step === 1 && (
          <div className="space-y-3">
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Title (e.g. Strategy Director — Final Round)" className="w-full border rounded-lg px-3 py-2 font-dm text-sm" data-testid="ab-title" />
            <input value={role} onChange={e => setRole(e.target.value)} placeholder="Role title" className="w-full border rounded-lg px-3 py-2 font-dm text-sm" />
            <select value={seniority} onChange={e => setSeniority(e.target.value)} className="w-full border rounded-lg px-3 py-2 font-dm text-sm">
              <option>Junior</option><option>Mid</option><option>Senior</option><option>Leadership</option>
            </select>
            <button onClick={() => title.length >= 5 ? setStep(2) : alert("Title too short")} className="bg-navy text-gold font-dm font-bold px-4 py-2 rounded-lg" data-testid="ab-next-1">Next →</button>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-3">
            <p className="font-dm text-navy/60 text-sm">Select skills to assess ({selectedSkills.length} selected)</p>
            <div className="max-h-96 overflow-y-auto border border-navy/10 rounded-lg p-2">
              {allSkills.map(s => (
                <label key={s.skill_code} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-navy/5 rounded px-2">
                  <input type="checkbox" checked={!!selectedSkills.find(x => x.skill_code === s.skill_code)} onChange={() => toggleSkill(s)} />
                  <span className="font-dm text-sm text-navy">{s.skill_name}</span>
                  <span className="font-dm text-xs text-navy/40 ml-auto">{s.dimension}</span>
                </label>
              ))}
            </div>
            <button onClick={() => { loadQuestions(); setStep(3); }} disabled={selectedSkills.length === 0} className="bg-navy text-gold font-dm font-bold px-4 py-2 rounded-lg disabled:opacity-50" data-testid="ab-next-2">Pick questions →</button>
          </div>
        )}
        {step === 3 && (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {selectedSkills.map(s => (
              <div key={s.skill_code} className="border border-navy/10 rounded-lg p-3">
                <p className="font-dm text-navy font-bold text-sm mb-2">{s.skill_name}</p>
                {(questionsBySkill[s.skill_code] || []).length === 0 ? (
                  <p className="font-dm text-navy/40 text-xs italic">No questions seeded for this skill yet — add one in the Question Bank tab.</p>
                ) : (
                  (questionsBySkill[s.skill_code] || []).map(q => (
                    <label key={q._id} className="flex items-start gap-2 py-1 cursor-pointer">
                      <input type="checkbox" checked={(selectedQs[s.skill_code] || []).includes(q._id)} onChange={() => toggleQ(s.skill_code, q._id)} className="mt-1" />
                      <span className="font-dm text-xs text-navy/80"><span className="font-bold">[{q.question_type}]</span> {q.question_text}</span>
                    </label>
                  ))
                )}
              </div>
            ))}
            <button onClick={save} className="bg-gold text-navy font-dm font-bold px-6 py-2 rounded-lg" data-testid="ab-save">Create Assessment</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function TaxonomyAdminTab() {
  const [tab, setTab] = useState("skills");
  return (
    <div className="space-y-5" data-testid="taxonomy-admin-tab">
      <div className="bg-white rounded-xl shadow-md p-5">
        <h2 className="font-playfair text-navy text-lg font-bold flex items-center gap-2"><BookOpen className="w-5 h-5 text-gold" /> Skills, Questions & Assessments</h2>
        <p className="font-dm text-navy/60 text-xs">Manage the 45-skill taxonomy + question bank + assessments. Behavioural anchors are generated on-demand via Claude.</p>
      </div>
      <SubTabs tab={tab} setTab={setTab} />
      {tab === "skills" && <SkillsTab />}
      {tab === "questions" && <QuestionsTab />}
      {tab === "assessments" && <AssessmentsTab />}
    </div>
  );
}
